// SignIn.jsx
import React, { useRef, useContext, useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { UserEmail } from "../../../App";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { userAuth } from "../../../firebase/firebase";
import { pageMetaTags } from "../metaTags/MetaData";
import MetaTags from "../metaTags/MetaTags";

// Import components
import NavBar from "./components/navbar";
import GoogleButton from "./components/google-button";
import OrDivider from "./components/OrDivider";
import LoginForm from "./components/loginform";
import TermsFooter from "./components/terms-footer";
import ModalContainer from "./components/modal-container";
import PrivacyModal from "./components/privacy-modal";
import PricingModal from "./components/pricing-modal";
import FAQModal from "./components/faq-modal";
import OverviewModal from "./components/overview-modal";

// Add CSS styles
const styles = `
  .signin-container {
    display: flex;
    min-height: 100vh;
    background-color: #fff;
    font-family: Helvetica, sans-serif;
    padding-top: 72px;
  }
  
  .signin-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 16px;
  }
  
  .login-section {
    flex: 1 1 auto;
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 24px 0;
    margin-bottom: 24px;
  }
  
  .login-heading {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.25;
    color: #000;
    margin-bottom: 8px;
  }
  
  .login-intro {
    font-size: 18px;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.5;
  }
  
  .video-section {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #111d4a;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    padding: 24px 16px;
    margin: 0 0 24px 0;
    color: #fff;
  }
  
  .video-control-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: rgba(0, 123, 255, 0.8);
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    opacity: 0.9;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 2;
    outline: none;
  }
  
  /* Media Queries */
  @media (min-width: 768px) {
    .signin-content {
      flex-direction: row;
      padding: 0 24px;
    }
    
    .login-section {
      flex: 1 1 50%;
      max-width: 480px;
      padding-right: 24px;
      margin-bottom: 0;
    }
    
    .login-heading {
      font-size: 32px;
    }
    
    .login-intro {
      font-size: 20px;
    }
    
    .video-section {
      flex: 1 1 50%;
      margin-left: 24px;
      padding: 24px;
    }
    
    .video-control-button {
      width: 72px;
      height: 72px;
    }
  }
  
  @media (min-width: 1024px) {
    .video-section {
      margin-left: 50px;
    }
  }
`;

function logout() {
  const auth = getAuth();
  signOut(auth)
    .then(() => {
      // Sign-out successful.
    })
    .catch((error) => {
      // An error happened.
    });
}

const SignIn = () => {
  const LoginContext = useContext(UserEmail);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const [currentModal, setCurrentModal] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Add window resize listener for responsive adjustments
  useEffect(() => {
    // Add CSS styles to head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    
    // Check screen size on mount and resize
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', checkScreenSize);
    checkScreenSize();
    
    // Add video event listeners
    const setupVideoControls = () => {
      const controlButton = document.getElementById('videoControlButton');
      const video = document.getElementById('demoVideo');
      const playIcon = document.getElementById('playIcon');
      const pauseIcon = document.getElementById('pauseIcon');
      
      if (controlButton && video && playIcon && pauseIcon) {
        // Initial state setup
        pauseIcon.style.display = 'none';
        
        // Toggle play/pause
        controlButton.addEventListener('click', function() {
          if (video.paused || video.ended) {
            // Play video
            if (video.readyState === 0) {
              video.load();
            }
            
            video.play().then(() => {
              // Update button to show pause icon
              playIcon.style.display = 'none';
              pauseIcon.style.display = 'block';
            }).catch(e => {
              console.error('Error playing video:', e);
            });
          } else {
            // Pause video
            video.pause();
            // Update button to show play icon
            playIcon.style.display = 'block';
            pauseIcon.style.display = 'none';
          }
        });
        
        // Update button when video ends
        video.addEventListener('ended', function() {
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
        });
        
        // Handle pause event (e.g., if user clicks directly on video)
        video.addEventListener('pause', function() {
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
        });
        
        // Handle play event (e.g., if user clicks directly on video)
        video.addEventListener('play', function() {
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
        });
      }
    };
    
    // Set up video controls after DOM is fully loaded
    if (document.readyState === 'complete') {
      setupVideoControls();
    } else {
      window.addEventListener('load', setupVideoControls);
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('load', setupVideoControls);
      document.head.removeChild(styleElement);
    };
  }, []);

  const signIn = (e) => {
    e.preventDefault();
    signInWithEmailAndPassword(
      userAuth,
      emailRef.current.value,
      passwordRef.current.value
    )
      .then((user) => {
        window.location.href = "/SpectralLibrary";
      })
      .catch((err) => {
        const errorCode = err.code;
        // console.logerrorCode);
        if (
          errorCode === "auth/user-not-found" ||
          errorCode === "auth/invalid-login-credentials" ||
          errorCode === "auth/invalid-email"
        ) {
          alert("Incorrect username. Please try again.");
        } else if (errorCode === "auth/wrong-password") {
          alert("Incorrect password. Please try again.");
        } else {
          alert("Contact site admin at j.levermore@imperial.ac.uk");
        }
      });
  };

  const closeModal = () => {
    setCurrentModal(null);
  };
  
  // Toggle video play/pause
  const toggleVideo = () => {
    const video = document.getElementById('demoVideo');
    const playIcon = document.getElementById('playIcon');
    const pauseIcon = document.getElementById('pauseIcon');
    
    if (video) {
      if (video.paused || video.ended) {
        video.play();
        if (playIcon && pauseIcon) {
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
        }
      } else {
        video.pause();
        if (playIcon && pauseIcon) {
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
        }
      }
    }
  };

  const meta = pageMetaTags.signIn;

  return (
    <>
      <MetaTags
        title={meta.title}
        description={meta.description}
        url={meta.url}
        image={meta.image}
        siteName={meta.siteName}
        twitterCard={meta.twitterCard}
        twitterSite={meta.twitterSite}
        fbAppId={meta.fbAppId}
      />
      
      {/* Navigation Bar */}
      <NavBar setCurrentModal={setCurrentModal} />

      {/* Main Content */}
      <div className="signin-container">
        <div className="signin-content">
          {/* Left side - Login content */}
          <div className="login-section">
            <h1 className="login-heading">
              Welcome to Plastic Analytics
            </h1>
            <p className="login-intro">
              Where we offer an AI-powered spectroscopic analysis framework to aid researchers rapidly identify, quantify, and monitor plastic compounds with unmatched precision.
            </p>

            <p style={{
              fontSize: "16px",
              fontWeight: "400",
              color: "#374151",
              marginBottom: "24px",
            }}>
              Sign in or create an account to access your personalised plastic spectral analysis!
            </p>

            <GoogleButton />
            <OrDivider />
            <LoginForm emailRef={emailRef} passwordRef={passwordRef} onSubmit={signIn} />
            <TermsFooter setCurrentModal={setCurrentModal} />
          </div>

          {/* Right side - Image/Video */}
          <div className="video-section">
            <div style={{
              marginBottom: "24px",
              textAlign: "center",
              padding: "0 16px",
            }}>
              <h3 style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#fff",
                marginBottom: "12px",
              }}>
                AI-Powered Spectroscopic Analysis
              </h3>
              <p style={{
                fontSize: "15px",
                color: "rgba(255, 255, 255, 0.85)",
                lineHeight: "1.5",
              }}>
                See how our platform helps researchers identify and quantify plastics with advanced machine learning algorithms.
              </p>
            </div>

            <div style={{
              width: "100%",
              position: "relative",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              backgroundColor: "#000", // Black background for video
            }}>
              {/* Video element */}
              <video
                id="demoVideo"
                preload="metadata"
                poster="/api/placeholder/800/600"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              >
                <source 
                  src="https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2FpromotionalVideo%2Fpromo-video.mp4?alt=media&token=8a2a57d2-7f94-4b24-a328-a8e026efd0ca" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
              
              {/* Combined play/pause button */}
              <button
                id="videoControlButton"
                className="video-control-button"
                onClick={toggleVideo}
                aria-label="Play/Pause video"
              >
                {/* Play icon */}
                <svg id="playIcon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                  <path d="M8 5V19L19 12L8 5Z" />
                </svg>
                
                {/* Pause icon */}
                <svg id="pauseIcon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                  <path d="M6 4H10V20H6V4Z" />
                  <path d="M14 4H18V20H14V4Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <ModalContainer isOpen={currentModal === "pricing"} closeModal={closeModal}>
        <PricingModal closeModal={closeModal} />
      </ModalContainer>
      
      <ModalContainer isOpen={currentModal === "privacy"} closeModal={closeModal}>
        <PrivacyModal closeModal={closeModal} />
      </ModalContainer>
      
      <ModalContainer isOpen={currentModal === "faq"} closeModal={closeModal}>
        <FAQModal closeModal={closeModal} />
      </ModalContainer>
      
      <ModalContainer isOpen={currentModal === "overview"} closeModal={closeModal}>
        <OverviewModal closeModal={closeModal} setCurrentModal={setCurrentModal} />
      </ModalContainer>
    </>
  );
};

export default SignIn;